import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PdfOfferUpload, type MatchedBudget } from "@/components/new-project/PdfOfferUpload";
import { StepBudget } from "@/components/new-project/StepBudget";
import { useImportUpsellFromPdf, type ImportUpsellLine } from "@/hooks/use-upsells";
import { SrIcon } from "@/components/ui/SrIcon";

/**
 * VIB-695-followup: Importér mersalg fra PDF/skjermbilde med samme verktøy
 * som "Nytt prosjekt → Tilbud og budsjett". Bruker `PdfOfferUpload` for
 * Vision-extraction og `StepBudget` for redigering før insert.
 *
 * Hele PDF-en lagres på upsell-raden via `useImportUpsellFromPdf` slik at
 * vedlegget kan vises igjen i MersalgTab og UpsellEditDialog.
 */
interface BudgetLine {
  id: string;
  phase: string;
  description: string;
  quantity_hours: number;
  amount: number;
  estimated_cost: number;
  has_time: boolean;
  cost_type: "internal" | "external";
  template_id: string | null;
  template_name: string | null;
}

interface Props {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UpsellImportFromPdfDialog({ projectId, open, onOpenChange }: Props) {
  const [pdfUploaded, setPdfUploaded] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [budgetLines, setBudgetLines] = useState<BudgetLine[]>([]);
  const [aiParsedValues, setAiParsedValues] = useState<Record<string, { amount: number }>>({});
  const [upsellTitle, setUpsellTitle] = useState("Mersalg fra import");
  const [offerOverheadPct, setOfferOverheadPct] = useState(0);
  const [offerDiscountPct, setOfferDiscountPct] = useState(0);
  const [offerDiscountAmount, setOfferDiscountAmount] = useState(0);

  const importMut = useImportUpsellFromPdf();

  // Reset state når dialogen lukkes så neste åpning starter blankt.
  useEffect(() => {
    if (!open) {
      setPdfUploaded(false);
      setUploadedFile(null);
      setBudgetLines([]);
      setAiParsedValues({});
      setUpsellTitle("Mersalg fra import");
      setOfferOverheadPct(0);
      setOfferDiscountPct(0);
      setOfferDiscountAmount(0);
    }
  }, [open]);

  // Pdf-opplasteren gir oss en File-instans via egen onChange — vi pakker den
  // bare inn i en wrapper rundt fileInput for å lagre referansen. Det er enklere
  // enn å plukke filen ut av onOfferSelected som ikke vet om filen.
  // Vi løser dette med en intercept-listener på selve filinput-elementet
  // (se under: handleFileCaptured).

  const applyOffer = (extracted: MatchedBudget) => {
    const aiValues: Record<string, { amount: number }> = {};
    const lines: BudgetLine[] = extracted.lines.map((item) => {
      const lineId = String(Math.random());
      aiValues[lineId] = { amount: item.amount };
      return {
        id: lineId,
        phase: item.effective_phase,
        description: item.description,
        quantity_hours: item.quantity_hours ?? 0,
        amount: item.amount,
        estimated_cost: 0,
        has_time: item.has_time,
        cost_type: item.effective_cost_type,
        template_id: item.template_match?.template_id ?? null,
        template_name: item.template_match?.template_name ?? null,
      };
    });
    setBudgetLines(lines);
    setAiParsedValues(aiValues);
    setOfferOverheadPct(extracted.overhead_pct ?? 0);
    setOfferDiscountPct(extracted.discount_pct ?? 0);
    setOfferDiscountAmount(extracted.discount_amount ?? 0);
    setPdfUploaded(true);
  };

  const handlePdfReset = () => {
    setPdfUploaded(false);
    setUploadedFile(null);
    setBudgetLines([]);
    setAiParsedValues({});
  };

  // Beregnede totaler — speiler StepBudget/BudgetSummary.
  const budgetTotal = budgetLines.reduce((s, l) => s + l.amount, 0);
  const totalEstCost = budgetLines.reduce((s, l) => s + l.estimated_cost, 0);
  const totalHours = budgetLines.reduce((s, l) => s + l.quantity_hours, 0);
  const effectiveDiscount =
    offerDiscountAmount > 0
      ? offerDiscountAmount
      : offerDiscountPct > 0
        ? (budgetTotal * (1 + offerOverheadPct / 100) * offerDiscountPct) / 100
        : 0;
  const calculatedTotal =
    budgetTotal + (budgetTotal * offerOverheadPct) / 100 - effectiveDiscount;

  const handleConfirm = async () => {
    if (budgetLines.length === 0) {
      toast.error("Ingen linjer å importere");
      return;
    }
    const lines: ImportUpsellLine[] = budgetLines.map((l) => ({
      phase: l.phase,
      description: l.description,
      quantity_hours: l.quantity_hours,
      amount: l.amount,
      estimated_cost: l.estimated_cost,
      cost_type: l.cost_type,
      template_id: l.template_id,
    }));
    try {
      await importMut.mutateAsync({
        project_id: projectId,
        description: upsellTitle,
        lines,
        overhead_pct: offerOverheadPct > 0 ? offerOverheadPct : null,
        discount_pct: offerDiscountPct,
        discount_amount: offerDiscountAmount,
        file: uploadedFile,
      });
      toast.success(`Mersalg-utkast opprettet med ${lines.length} linje(r)`);
      onOpenChange(false);
    } catch {
      // Toast vises allerede via mutation onError
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importer mersalg fra PDF</DialogTitle>
          <DialogDescription>
            Last opp PDF eller skjermbilde fra kunden. Claude Vision analyserer
            budsjettet og matcher linjene mot mal-katalogen, akkurat som ved
            "Nytt prosjekt". PDF-en lagres på mersalg-raden så den kan vises
            igjen senere.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* VIB-695-followup: Vi wrapper PdfOfferUpload i en div med onChange-
              capture for å fange selve filen — komponenten håndterer Vision-
              kallet selv, men vi trenger File-instansen for å laste den opp
              til storage etterpå. */}
          <div
            onChangeCapture={(e) => {
              const target = e.target as HTMLInputElement;
              if (target.type === "file" && target.files && target.files.length > 0) {
                // Lagre første fil — multi-file håndteres ikke for mersalg
                // (kunden sender typisk én PDF / ett skjermbilde per mersalg).
                setUploadedFile(target.files[0]);
              }
            }}
          >
            <PdfOfferUpload
              onOfferSelected={applyOffer}
              pdfUploaded={pdfUploaded}
              budgetLinesCount={budgetLines.length}
              onReset={handlePdfReset}
              aiProgress={0}
              aiLoading={false}
            />
          </div>

          {pdfUploaded && budgetLines.length > 0 && (
            <>
              <div>
                <Label htmlFor="upsell-title">Tittel (vises på faktura)</Label>
                <Input
                  id="upsell-title"
                  value={upsellTitle}
                  onChange={(e) => setUpsellTitle(e.target.value)}
                  placeholder="F.eks. Ekstra klippedag oktober"
                />
              </div>

              <StepBudget
                budgetLines={budgetLines}
                aiParsedValues={aiParsedValues}
                onUpdateBudgetLine={(line) =>
                  setBudgetLines((prev) =>
                    prev.map((l) => (l.id === line.id ? { ...l, ...line } : l)),
                  )
                }
                onDeleteBudgetLine={(id) =>
                  setBudgetLines((prev) => prev.filter((l) => l.id !== id))
                }
                onAddBudgetLine={(phase) =>
                  setBudgetLines((prev) => [
                    ...prev,
                    {
                      id: String(Math.random()),
                      phase,
                      description: "",
                      quantity_hours: 0,
                      amount: 0,
                      estimated_cost: 0,
                      has_time: false,
                      cost_type: "external",
                      template_id: null,
                      template_name: null,
                    },
                  ])
                }
                budgetTotal={budgetTotal}
                totalEstCost={totalEstCost}
                totalHours={totalHours}
                calculatedTotal={calculatedTotal}
                validationDiff={0}
                offerOverheadPct={offerOverheadPct}
                offerDiscountPct={offerDiscountPct}
                offerDiscountAmount={offerDiscountAmount}
              />
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={importMut.isPending || budgetLines.length === 0}
          >
            {importMut.isPending ? (
              <>
                <SrIcon name="loader2" className="w-4 h-4 mr-2 animate-spin" />
                Oppretter mersalg…
              </>
            ) : (
              <>
                Opprett mersalg-utkast ({budgetLines.length} linje
                {budgetLines.length === 1 ? "" : "r"})
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
